import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "@/lib/toast";
import { triggerHaptic } from "@/lib/haptics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import StatusBadge, { STATUS_OPTIONS } from "@/components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Milestones from "@/components/Milestones";
import Timeline from "@/components/Timeline";
import BookingForm from "@/components/BookingForm";
import BatchesPanel from "@/components/BatchesPanel";
import TrackingPill from "@/components/TrackingPill";
import StatCard from "@/components/StatCard";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { AwbLabel, PodDocument, ProformaInvoice } from "@/components/ShipmentDocs";
import { useSource } from "@/context/SourceContext";
import { useNav } from "@/components/DashboardShell";
import { Package, Truck, MapPin, Phone, Loader2, CheckCircle2, ClipboardList, Search, Pencil, Download, PackagePlus, X, FileText } from "lucide-react";

export default function CourierDashboard() {
  const { section } = useNav();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [trackFor, setTrackFor] = useState(null);
  const [trackForm, setTrackForm] = useState({ domestic_courier: "", pickup_tracking_number: "" });
  const [statusFor, setStatusFor] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "", location: "", date: "", note: "" });
  const [selected, setSelected] = useState([]);
  const toggleSel = (id) => { triggerHaptic("tap"); setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id])); };
  const [bookOpen, setBookOpen] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [search, setSearch] = useState("");
  const [courierDoc, setCourierDoc] = useState(null);
  const { source } = useSource();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders");
      setOrders(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const q = search.trim().toLowerCase();
  const inSource = (o) => !source || source === "ALL" || o.source_portal === source;
  const match = (o) => inSource(o) && (!q || `${o.tracking_number || ""} ${o.awb_number || ""} ${o.recipient_name || ""}`.toLowerCase().includes(q));
  const mine = orders.filter((o) => o.booked_by_courier && match(o));
  const assigned = orders.filter((o) => !o.booked_by_courier && match(o));
  const completed = assigned.filter((o) => o.warehouse_received);
  const active = assigned.filter((o) => !o.warehouse_received && o.status !== "Delivered" && o.status !== "Cancelled");
  const pending = active.filter((o) => !o.pickup_scheduled && !o.picked_up);
  const inProgress = active.filter((o) => o.pickup_scheduled || o.picked_up);

  const markPickup = async (o) => {
    setSaving(true);
    try {
      await api.post(`/courier/orders/${o.id}/pickup`);
      toast.success(`${o.tracking_number} marked Picked Up`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const openTrack = (o) => { setTrackFor(o); setTrackForm({ domestic_courier: o.domestic_courier || "", pickup_tracking_number: o.pickup_tracking_number || "" }); };
  const openBulkTrack = () => { setTrackFor({ bulk: true }); setTrackForm({ domestic_courier: "", pickup_tracking_number: "" }); };
  const doTrack = async () => {
    if (!trackForm.domestic_courier) return;
    setSaving(true);
    try {
      if (trackFor.bulk) {
        const { data } = await api.post("/courier/orders/tracking/bulk", { order_ids: selected, ...trackForm });
        toast.success(`Updated ${data.updated} shipment(s)${data.skipped ? `, ${data.skipped} skipped` : ""}`);
        setSelected([]);
      } else {
        await api.post(`/courier/orders/${trackFor.id}/tracking`, trackForm);
        toast.success("Tracking info saved");
      }
      setTrackFor(null); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const openStatus = (o) => { setStatusFor(o); setStatusForm({ status: "", location: "", date: new Date().toISOString().slice(0, 10), note: "" }); };
  const openBulkStatus = () => { setStatusFor({ bulk: true }); setStatusForm({ status: "", location: "", date: new Date().toISOString().slice(0, 10), note: "" }); };
  const doStatus = async () => {
    if (!statusForm.status) return;
    setSaving(true);
    try {
      if (statusFor.bulk) {
        const { data } = await api.post("/courier/orders/status/bulk", { order_ids: selected, ...statusForm });
        toast.success(`Updated ${data.updated} shipment(s)${data.skipped ? `, ${data.skipped} skipped` : ""}`);
        setSelected([]);
      } else {
        await api.post(`/courier/orders/${statusFor.id}/status`, statusForm);
        toast.success("Status updated");
      }
      setStatusFor(null); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const Card = ({ o, i = 0 }) => {
    const done = o.warehouse_received;
    const delivered = /^delivered$/i.test(o.status || "");
    const locked = done || delivered;
    const stripe = done ? "from-emerald-500 to-teal-300" : o.booked_by_courier ? "from-orange-500 to-amber-300" : (o.pickup_scheduled || o.picked_up) ? "from-cyan-500 to-sky-300" : "from-primary to-cyan-400";
    const isSel = selected.includes(o.id);
    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 8) * 0.04, duration: 0.3 }}
        className={`panel hover-lift relative flex flex-col gap-3 overflow-hidden p-5 ${isSel ? "border-primary/50 ring-2 ring-primary/25" : ""}`} data-testid={`pickup-card-${o.tracking_number}`}>
        <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${stripe}`} />
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2.5 min-w-0">
            {!done && (
              <input type="checkbox" className="mt-1 h-4 w-4 rounded border-border" checked={isSel} onChange={() => toggleSel(o.id)} data-testid={`courier-select-${o.tracking_number}`} />
            )}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <TrackingPill tn={o.tracking_number} />
                {o.booked_by_courier && <span className="inline-block rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-orange-700" data-testid={`courier-booked-tag-${o.tracking_number}`}>Booked by you</span>}
              </div>
              <div className="mt-1 truncate font-display text-lg font-bold tracking-tight">{o.user_name || o.customer_name}</div>
            </div>
          </div>
          <StatusBadge status={o.status} testId={`courier-status-${o.tracking_number}`} />
        </div>
        {done && (
          <div className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700" data-testid={`courier-completed-${o.tracking_number}`}>
            <CheckCircle2 className="h-4 w-4" /> Completed — received at New Delhi hub
          </div>
        )}
        <div className="space-y-1.5 text-sm text-muted-foreground">
          <div className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> <span>{o.pickup_address}</span></div>
          <div className="flex items-center gap-2"><Phone className="h-4 w-4 shrink-0 text-primary" /> <a href={`tel:${o.customer_phone}`} className="hover:text-primary hover:underline" onClick={() => triggerHaptic("tap")}>{o.customer_phone}</a></div>
          <div className="flex items-center gap-2"><Package className="h-4 w-4 shrink-0 text-primary" /> {o.box_count} box(es) · {o.weight} kg</div>
          {o.pickup_date && <div className="flex items-center gap-2"><ClipboardList className="h-4 w-4 shrink-0 text-primary" /> Pickup date: {o.pickup_date}</div>}
          {o.domestic_courier && <div className="flex items-center gap-2 text-foreground"><Truck className="h-4 w-4 shrink-0 text-primary" /> {o.domestic_courier}{o.pickup_tracking_number ? ` · ${o.pickup_tracking_number}` : ""}</div>}
        </div>
        <div className="mt-auto flex flex-wrap gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={() => setDetail(o)} data-testid={`courier-view-${o.tracking_number}`}>Details</Button>
          {!locked && (
            <Button variant="outline" size="sm" onClick={() => { setEditOrder(o); setBookOpen(true); }} data-testid={`courier-edit-${o.tracking_number}`}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
          )}
          {!locked && (
            <Button variant="outline" size="sm" onClick={() => openTrack(o)} data-testid={`courier-tracking-btn-${o.tracking_number}`}><Truck className="h-4 w-4" /> Tracking Info</Button>
          )}
          {!locked && (
            <Button variant="outline" size="sm" onClick={() => openStatus(o)} data-testid={`courier-status-btn-${o.tracking_number}`}><MapPin className="h-4 w-4" /> Update Status</Button>
          )}
          {!locked && !o.picked_up && !o.pickup_scheduled && (
            <Button size="sm" className="flex-1 shine-hover" haptic="success" disabled={saving} onClick={() => markPickup(o)} data-testid={`mark-pickup-${o.tracking_number}`}>
              <CheckCircle2 className="h-4 w-4" /> Mark Picked Up
            </Button>
          )}
        </div>
      </motion.div>
    );
  };

  const Section = ({ icon: Icon, tone, title, list, testId, empty }) => (
    <div data-testid={testId}>
      <h2 className="mb-3 flex items-center gap-2 text-base font-semibold">
        <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${tone}`}><Icon className="h-4 w-4" /></span>
        {title}
        <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-bold text-muted-foreground">{list.length}</span>
      </h2>
      {list.length === 0 ? <div className="rounded-xl border border-dashed border-border px-4 py-5 text-center text-sm text-muted-foreground">{empty}</div> : (
        <div className="grid gap-4 md:grid-cols-2">{list.map((o, i) => <Card key={o.id} o={o} i={i} />)}</div>
      )}
    </div>
  );

  const bookDialog = (
    <Dialog open={bookOpen} onOpenChange={(v) => { if (!v) { setBookOpen(false); setEditOrder(null); } }}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="courier-book-dialog">
        <DialogHeader><DialogTitle className="text-2xl">{editOrder ? `Edit Shipment · ${editOrder.tracking_number}` : "Book a Shipment"}</DialogTitle>{!editOrder && <DialogDescription>This shipment will be auto-assigned to you and tagged as booked by you.</DialogDescription>}</DialogHeader>
        <BookingForm key={editOrder?.id || "new"} courier editOrder={editOrder} onCreated={() => { setBookOpen(false); setEditOrder(null); load(); }} />
      </DialogContent>
    </Dialog>
  );

  if (section === "batches") return <div data-testid="courier-dashboard"><BatchesPanel /></div>;

  return (
    <div className="space-y-6" data-testid="courier-dashboard">
      <PageHeader eyebrow="Field Pickup" tone="orange" title="Pickup Runs" subtitle="Your bookings, assigned pickups and hub hand-offs — all in one run sheet."
        actions={<Button size="lg" className="shine-hover" onClick={() => { setEditOrder(null); setBookOpen(true); }} data-testid="courier-book-button"><PackagePlus className="h-4 w-4" /> Book Shipment</Button>} />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Assigned" value={assigned.length} icon={Package} tone="blue" delay={0} testId="courier-stat-assigned" />
        <StatCard label="To Pick Up" value={pending.length} icon={ClipboardList} tone="orange" delay={1} testId="courier-stat-pending" />
        <StatCard label="In Progress" value={inProgress.length} icon={Truck} tone="cyan" delay={2} testId="courier-stat-progress" />
        <StatCard label="Completed" value={completed.length} icon={CheckCircle2} tone="emerald" delay={3} testId="courier-stat-completed" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1 min-w-[240px] glow-ring" data-testid="courier-search">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search AWB / consignee…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="courier-search-input" />
          {search && <button type="button" onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-secondary hover:text-foreground" data-testid="courier-search-clear"><X className="h-3.5 w-3.5" /></button>}
        </div>
        {q && <span className="text-xs text-muted-foreground">{mine.length + assigned.length} match{mine.length + assigned.length === 1 ? "" : "es"}</span>}
      </div>

      {selected.length > 0 && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="glass sticky top-16 z-20 flex flex-wrap items-center gap-2 rounded-2xl border border-primary/25 px-4 py-2.5 shadow-[0_16px_40px_-20px_rgba(33,79,216,.5)]" data-testid="courier-bulk-toolbar">
          <span className="flex items-center gap-2 text-sm font-semibold"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-white">{selected.length}</span> selected</span>
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            <Button size="sm" variant="outline" onClick={openBulkStatus} data-testid="courier-bulk-status-button"><MapPin className="h-4 w-4" /> Update Status</Button>
            <Button size="sm" variant="outline" onClick={openBulkTrack} data-testid="courier-bulk-tracking-button"><Truck className="h-4 w-4" /> Tracking Info</Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected([])} data-testid="courier-bulk-clear">Clear</Button>
          </div>
        </motion.div>
      )}

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2">{[0, 1, 2, 3].map((i) => <div key={i} className="panel h-48 animate-pulse bg-slate-100/60" />)}</div>
      ) : orders.length === 0 ? (
        <EmptyState icon={Truck} title="No pickups assigned yet" hint="Shipments assigned to you by the admin — or booked by you — will appear here." testId="courier-empty"
          action={<Button onClick={() => { setEditOrder(null); setBookOpen(true); }}><PackagePlus className="h-4 w-4" /> Book a shipment</Button>} />
      ) : (
        <div className="space-y-8">
          {mine.length > 0 && <Section icon={Package} tone="bg-orange-500/10 text-orange-600" title="My Bookings" list={mine} testId="courier-section-mine" empty="" />}
          <Section icon={ClipboardList} tone="bg-primary/10 text-primary" title="To Pick Up" list={pending} testId="courier-section-pending" empty="Nothing pending — all caught up." />
          {inProgress.length > 0 && <Section icon={Truck} tone="bg-cyan-500/10 text-cyan-600" title="In Progress" list={inProgress} testId="courier-section-progress" empty="" />}
          {completed.length > 0 && <Section icon={CheckCircle2} tone="bg-emerald-500/10 text-emerald-600" title="Completed" list={completed} testId="courier-section-completed" empty="" />}
        </div>
      )}

      <Dialog open={!!trackFor} onOpenChange={(v) => { if (!v) setTrackFor(null); }}>
        <DialogContent data-testid="courier-tracking-dialog">
          <DialogHeader><DialogTitle className="text-2xl">Add Tracking Info</DialogTitle><DialogDescription>{trackFor?.bulk ? `Apply to ${selected.length} selected shipment(s)` : `Order ${trackFor?.tracking_number} · first-mile pickup courier details`}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Courier Name</Label>
              <Input value={trackForm.domestic_courier} onChange={(e) => setTrackForm((f) => ({ ...f, domestic_courier: e.target.value }))} placeholder="e.g. Delhivery, BlueDart, DTDC" data-testid="courier-track-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Pickup Tracking Number <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <Input value={trackForm.pickup_tracking_number} onChange={(e) => setTrackForm((f) => ({ ...f, pickup_tracking_number: e.target.value }))} placeholder="e.g. DL123456789" data-testid="courier-track-number-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={doTrack} disabled={saving || !trackForm.domestic_courier} data-testid="confirm-courier-tracking-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Tracking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!statusFor} onOpenChange={(v) => { if (!v) setStatusFor(null); }}>
        <DialogContent data-testid="courier-status-dialog">
          <DialogHeader><DialogTitle className="text-2xl">Update Status</DialogTitle><DialogDescription>{statusFor?.bulk ? `Apply to ${selected.length} selected shipment(s)` : `Order ${statusFor?.tracking_number} — you can update the status until it's received at the warehouse.`}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Status</Label><Select value={statusForm.status} onValueChange={(v) => setStatusForm((f) => ({ ...f, status: v }))}><SelectTrigger data-testid="courier-status-input"><SelectValue placeholder="Choose stage" /></SelectTrigger><SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Location <span className="font-normal text-muted-foreground">(optional)</span></Label><Input value={statusForm.location} onChange={(e) => setStatusForm((f) => ({ ...f, location: e.target.value }))} placeholder="e.g. Mumbai" data-testid="courier-status-location-input" /></div>
              <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={statusForm.date} onChange={(e) => setStatusForm((f) => ({ ...f, date: e.target.value }))} data-testid="courier-status-date-input" /></div>
            </div>
            <div className="space-y-1.5"><Label>Note <span className="font-normal text-muted-foreground">(optional)</span></Label><Input value={statusForm.note} onChange={(e) => setStatusForm((f) => ({ ...f, note: e.target.value }))} data-testid="courier-status-note-input" /></div>
          </div>
          <DialogFooter>
            <Button onClick={doStatus} disabled={saving || !statusForm.status} data-testid="confirm-courier-status-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="courier-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <div className="flex flex-wrap items-center gap-2"><TrackingPill tn={detail.tracking_number} /><StatusBadge status={detail.status} /></div>
                <DialogTitle className="text-2xl">{detail.user_name || detail.customer_name}</DialogTitle>
              </DialogHeader>
              <Milestones order={detail} testId="courier-milestones" />
              <div className="grid gap-3 text-sm sm:grid-cols-2">
                <div className="rounded-xl border border-border bg-slate-50/60 p-3"><div className="mb-1 text-[10px] font-mono-label uppercase tracking-widest text-muted-foreground">Pickup</div>{detail.pickup_address}</div>
                <div className="rounded-xl border border-border bg-slate-50/60 p-3"><div className="mb-1 text-[10px] font-mono-label uppercase tracking-widest text-muted-foreground">Deliver to</div>{detail.delivery_address}</div>
              </div>
              {detail.boxes && detail.boxes.length > 0 && (
                <div className="space-y-1 rounded-xl border border-border p-3 text-sm">
                  <div className="font-semibold">{detail.box_count} box(es) · {detail.weight} kg total</div>
                  {detail.boxes.map((b, i) => (
                    <div key={i} className="text-xs text-muted-foreground">Box {i + 1}: {b.length}×{b.width}×{b.height} cm · {b.weight} kg · {b.item_content}</div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2 rounded-xl border border-dashed border-border bg-slate-50/50 p-3">
                <span className="mr-1 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><FileText className="h-3.5 w-3.5" /> Documents</span>
                <Button size="sm" variant="outline" onClick={() => setCourierDoc({ o: detail, t: "awb" })} data-testid="courier-awb-btn">AWB Label</Button>
                <Button size="sm" variant="outline" onClick={() => setCourierDoc({ o: detail, t: "proforma" })} data-testid="courier-proforma-btn">Proforma Invoice</Button>
                {detail.status === "Delivered" && <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700" onClick={() => setCourierDoc({ o: detail, t: "pod" })} data-testid="courier-pod-btn">Proof of Delivery</Button>}
              </div>
              <Timeline timeline={detail.timeline} status={detail.status} order={detail} />
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!courierDoc} onOpenChange={(v) => { if (!v) setCourierDoc(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid="courier-doc-dialog">
          <DialogHeader className="no-print"><DialogTitle className="text-2xl">{courierDoc?.t === "pod" ? "Proof of Delivery" : courierDoc?.t === "proforma" ? "Proforma Invoice" : "AWB Label"}</DialogTitle></DialogHeader>
          {courierDoc && (courierDoc.t === "pod" ? <PodDocument order={courierDoc.o} /> : courierDoc.t === "proforma" ? <ProformaInvoice order={courierDoc.o} /> : <AwbLabel order={courierDoc.o} />)}
          <DialogFooter className="no-print"><Button onClick={() => window.print()} data-testid="courier-doc-print"><Download className="h-4 w-4" /> Download / Print</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {bookDialog}
    </div>
  );
}
